// src/components/DdosTesting/ProtectionAnalysis.jsx
import React from 'react';

import { motion } from 'framer-motion'; // Import motion

// Accept protections and recommendations as props
const ProtectionAnalysis = ({ protections = [], recommendations = [] }) => {

    // Determine if data is available
    const hasProtectionData = protections && protections.length > 0;
    const hasRecommendationData = recommendations && recommendations.length > 0;

    // Return null or a placeholder if no data exists at all
    if (!hasProtectionData && !hasRecommendationData) {
        return null; // Or return a placeholder component if desired
    }

    // Map status to color/icon - Adjust based on your actual data structure
    const getProtectionStatus = (status) => {
        switch(status?.toLowerCase()){
            case 'active':
            case 'detected':
                return { color: 'green', icon: 'fa-check-circle' };
            case 'partial':
            case 'suspected':
                return { color: 'yellow', icon: 'fa-exclamation-triangle' };
            case 'inactive':
            case 'bypassed':
                return { color: 'red', icon: 'fa-times-circle' };
            default:
                return { color: 'blue', icon: 'fa-question-circle' }; // Default/unknown
        }
    };

    return (
        <motion.div // Animate the appearance
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }} // Add slight delay
            className="bg-dark-600 rounded-2xl p-6 mb-8 border border-dark-400 hover:border-accent-green/30 transition-all hover:shadow-lg relative overflow-hidden"
        >
            {/* Background effect */}
            <div className="absolute -right-10 -top-10 w-32 h-32 bg-accent-green opacity-5 rounded-full blur-xl group-hover:opacity-10 transition-medium"></div>

            <h3 className="font-bold text-lg mb-5 flex items-center group relative z-10">
                <div className="w-8 h-8 rounded-lg bg-accent-green/20 flex items-center justify-center mr-2 transform-gpu group-hover:rotate-12 transition-medium">
                   <i className="fas fa-shield-alt text-accent-green"></i>
                </div>
                DDoS Protection Analysis
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10">
                {/* Detected Protection */}
                <motion.div
                   initial={{ x: -10, opacity: 0 }}
                   animate={{ x: 0, opacity: 1 }}
                   transition={{ delay: 0.2 }}
                   className="bg-dark-500 rounded-xl p-5 border border-dark-400 hover:border-dark-300 transition"
                >
                    <h4 className="font-medium mb-4 text-white">Detected Protection</h4>
                    {!hasProtectionData ? (
                        <p className="text-gray-400 text-sm">No protection data available</p>
                    ) : (
                        <div className="space-y-4">
                            {protections.map((protection, index) => {
                                const statusInfo = getProtectionStatus(protection.status);
                                return (
                                    <div key={index} className="flex items-center group/item">
                                        <div className={`w-8 h-8 rounded-lg bg-accent-${statusInfo.color}/20 flex items-center justify-center mr-3 flex-shrink-0 transition-transform group-hover/item:scale-110`}>
                                            <i className={`fas ${statusInfo.icon} text-accent-${statusInfo.color}`}></i>
                                        </div>
                                        <div>
                                            <p className="font-medium group-hover/item:text-white transition">{protection.name || 'Unknown Protection'}</p>
                                            <p className="text-xs text-gray-400 group-hover/item:text-gray-300 transition">{protection.description || 'No description'}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </motion.div>

                {/* Recommendations */}
                 <motion.div
                     initial={{ x: 10, opacity: 0 }}
                     animate={{ x: 0, opacity: 1 }}
                     transition={{ delay: 0.3 }}
                     className="bg-dark-500 rounded-xl p-5 border border-dark-400 hover:border-dark-300 transition"
                 >
                    <h4 className="font-medium mb-4 text-white">Recommendations</h4>
                    {!hasRecommendationData ? (
                        <p className="text-gray-400 text-sm">No recommendations available</p>
                    ) : (
                        <div className="space-y-4">
                            {recommendations.map((rec, index) => (
                                <div key={index} className="flex group/item">
                                    <div className="w-8 h-8 rounded-lg bg-accent-blue/20 flex items-center justify-center mr-3 flex-shrink-0 mt-1 transition-transform group-hover/item:scale-110">
                                        <i className="fas fa-info-circle text-accent-blue"></i> {/* Consistent icon */}
                                    </div>
                                    <div>
                                        <p className="font-medium group-hover/item:text-white transition">{rec.title || 'Recommendation'}</p>
                                        <p className="text-xs text-gray-400 group-hover/item:text-gray-300 transition">{rec.description || 'No details provided.'}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </motion.div>
            </div>
        </motion.div>
    );
};

export default ProtectionAnalysis;